import React from 'react';
import { MdStore, MdCreditCard } from 'react-icons/md';
import { FormData, FormErrors } from '../../hooks/useCheckoutForm';

interface PickupFormProps {
  formData: FormData;
  errors: FormErrors;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onTakeoutOrder: () => void;
  orderLoading: boolean;
  submittingMethod: 'takeout' | 'pay_online' | 'delivery' | null;
  cartItemsCount: number;
  showPayNow?: boolean;
  showPayLater?: boolean;
}

export const PickupForm: React.FC<PickupFormProps> = ({
  formData,
  errors,
  handleChange,
  onSubmit,
  onTakeoutOrder,
  orderLoading,
  submittingMethod,
  cartItemsCount,
  showPayNow = true,
  showPayLater = true,
}) => {
  return (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      {/* Personal Information - 2 Column Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your full name"
            aria-describedby={errors.name ? 'name-error' : undefined}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p id="name-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your phone number"
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address (Optional)
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your email address (optional)"
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-1">
          Special Requests (Optional)
        </label>
        <textarea
          id="specialRequests"
          name="specialRequests"
          value={formData.specialRequests}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900"
          placeholder="Any special instructions for your order"
        />
      </div>

      {/* Pickup Action Buttons */}
      <div className={`pt-4 grid grid-cols-1 ${showPayNow && showPayLater ? 'sm:grid-cols-2' : ''} gap-4`}>
        {showPayLater && (
          <button
            type="button"
            disabled={orderLoading || cartItemsCount === 0}
            className={`bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center ${
              (orderLoading || cartItemsCount === 0) ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            onClick={onTakeoutOrder}
          >
            {orderLoading && submittingMethod === 'takeout' ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" aria-hidden="true"></div>
            ) : (
              <MdStore className="w-5 h-5 mr-2" aria-hidden="true" />
            )}
            Takeout
          </button>
        )}
        {showPayNow && (
          <button
            type="submit"
            disabled={orderLoading || cartItemsCount === 0}
            className={`bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center ${
              (orderLoading || cartItemsCount === 0) ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {orderLoading && submittingMethod === 'pay_online' ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" aria-hidden="true"></div>
            ) : (
              <MdCreditCard className="w-5 h-5 mr-2" aria-hidden="true" />
            )}
            Pay Now
          </button>
        )}
      </div>
    </form>
  );
};
